import {
  ArrowLeftRight,
  Briefcase,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Globe,
  ListChecks,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import BarFill from '@/components/charts/BarFill'
import SegmentedBar from '@/components/ui/SegmentedBar'
import Reveal from '@/components/motion/Reveal'
import IconButton from '@/components/ui/IconButton'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import {
  barShare,
  compactDigits,
  compareValues,
  contributionsOf,
  coverageWords,
  PAGE_SIZE,
  nounOf,
  shownValue,
  warningPair,
  partialNote,
  type Part,
} from '@/features/dashboard/lib/format'
import { pageOf, usePage } from '@/features/dashboard/lib/page'
import { aggregatesOf, labelsOf, pictureText } from '@/features/dashboard/lib/picture'
import { hasMeta, partial, topShare, type Share } from '@/features/dashboard/lib/signal'
import { isWindowed } from '@/features/dashboard/lib/time'
import type {
  BreakdownPart,
  Check,
  Coverage,
  Drawn,
  Fix,
  Point,
  ScopeRows,
} from '@/features/mitra/api/dashboard'
import { Mitramark } from '@/features/mitra/panel/Mitramark'
import { cn } from '@/lib/utils'
import FormulaPicture from '@/screens/dashboards/components/FormulaPicture'
import { WindowCard } from '@/screens/dashboards/components/TimeCards'

/**
 * How a dashboard widget draws, visual first (owner, round 3: "too much text
 * … easy to understand at one view … visualize").
 *
 * A CARD LEADS WITH ITS ONE NUMBER OR CHART. Under the title, the formula's
 * picture in small chips; then the value large. Anything else is drawn only
 * where it says something the card does not already: a coverage ring only
 * where some rows are left out, a share bar only where the biggest parts are
 * not simply all of them, a warning as one blue chip with a Fix. No figure is
 * drawn twice on a card. Opening a figure shows each company's part of it as
 * bars, largest first, with the companies it left out greyed.
 *
 * EVERY VALUE IS THE ENGINE'S. A bar's length is drawing only; the digits
 * beside it are the engine's own strings.
 */

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

/** "Made by Mitra", as the mascot's dot: the words are its name and tooltip. */
export function MitraDot({ origin }: { origin: Drawn['origin'] }) {
  if (origin !== 'mitra') return null
  return (
    <span
      data-role="made-by-mitra"
      role="img"
      aria-label="Made by Mitra"
      title="Made by Mitra"
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-tone-info-soft"
    >
      <Mitramark size={16} className="size-4" />
    </span>
  )
}

/**
 * How many of the rows a value stands on, as a small ring and "51 of 60
 * holdings" -- only where some are left out. Every row counted is the norm and
 * says nothing, so it draws nothing.
 */
export function CoverageMeter({ coverage, noun }: { coverage: Coverage; noun: string }) {
  if (!partial(coverage)) return null
  const words = `Counts ${String(coverage.counted)} of ${String(coverage.total)} ${noun}`
  const ratio = coverage.counted / coverage.total
  // Drawing only: the ring's arc is the share of rows the value stands on.
  const circumference = 2 * Math.PI * 9
  return (
    <span
      data-role="coverage"
      role="img"
      aria-label={words}
      title={words}
      className="inline-flex min-w-0 items-center gap-1"
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5 shrink-0 -rotate-90">
        <circle cx="12" cy="12" r="9" fill="none" strokeWidth="3" className="stroke-muted" />
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${String(ratio * circumference)} ${String(circumference)}`}
          className="stroke-warning transition-[stroke-dasharray] duration-slow ease-out"
        />
      </svg>
      <span aria-hidden="true" className="truncate text-label-m text-warning tabular-nums">
        {coverageWords(coverage, noun)}
      </span>
    </span>
  )
}

/** What the check-up flagged, as one blue chip: the two numbers and a Fix. */
export function WarningChip({
  check,
  onApply,
}: {
  check: Check | null
  onApply?: ((fix: Fix) => void) | undefined
}) {
  const first = check?.warnings[0]
  if (!check || !first) return null
  const pair = warningPair(first)
  const more = check.warnings.length - 1
  return (
    <span
      data-role="warning-chip"
      className="inline-flex max-w-full items-center gap-1 rounded-full bg-warning-soft py-1 pr-1 pl-2 text-label-s text-warning"
    >
      <span
        role="img"
        aria-label={first.message}
        title={first.message}
        className="inline-flex min-w-0 items-center gap-1"
      >
        <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-warning" />
        <span aria-hidden="true" className="truncate tabular-nums">
          {pair ?? 'Check'}
          {more > 0 ? ` +${String(more)}` : ''}
        </span>
      </span>
      {first.fix && onApply ? (
        <button
          type="button"
          data-role="warning-fix"
          aria-label={`Fix: ${first.fix.label || first.message}`}
          onClick={() => {
            if (first.fix) onApply(first.fix)
          }}
          className={cn(
            'min-h-6 rounded-full bg-card px-2 text-label-s text-foreground hover:bg-muted',
            FOCUS,
          )}
        >
          Fix
        </button>
      ) : null}
    </span>
  )
}

/**
 * The formula's picture, or the formula itself where the engine drew none, in
 * a tinted panel straight under the title: what a measure IS is the first
 * thing the eye should land on, not the last (owner, 2026-10-18).
 */
export function Picture({ widget, size = 'sm' }: { widget: Drawn; size?: 'sm' | 'lg' }) {
  const meaning = widget.check?.explanation ?? widget.formula
  return (
    <div data-role="picture-panel" className={cn('mt-2 min-w-0', size === 'lg' && 'mt-3')}>
      {widget.check?.picture ? (
        <FormulaPicture picture={widget.check.picture} label={meaning} size={size} />
      ) : (
        <code
          title={meaning}
          className="block font-mono text-body-xs break-words text-muted-foreground"
        >
          {widget.formula}
        </code>
      )}
    </div>
  )
}

/** A widget whose formula the engine could not read: the reason, never a value. */
export function Unreadable({ widget }: { widget: Drawn }) {
  return (
    <p role="status" data-role="formula-error" className="mt-1 text-body-s text-destructive">
      {widget.error?.message}
    </p>
  )
}

export function Head({ widget, actions }: { widget: Drawn; actions: ReactNode }) {
  return (
    <>
      <div className="flex items-start gap-2">
        <h3 className="min-w-0 flex-1 text-label-l break-words text-foreground">{widget.title}</h3>
        <MitraDot origin={widget.origin} />
        {/* The 32px ⋮ centred on the title's 20px line, its glyph on the card's right content edge. */}
        {actions ? <div className="-mr-2 flex h-5 shrink-0 items-center">{actions}</div> : null}
      </div>
      {/* Adjusted from what was asked: said, quietly, so a corrected formula is never silent. */}
      {widget.fixedFrom ? (
        <p data-role="adjusted-note" className="mt-1 text-label-s text-muted-foreground">
          Adjusted: written as {widget.fixedFrom}
        </p>
      ) : null}
    </>
  )
}

/** The chips under a value: coverage and what to check, where either says something. */
export function Meta({
  widget,
  noun,
  onApplyFix,
}: {
  widget: Drawn
  noun: string
  onApplyFix?: ((fix: Fix) => void) | undefined
}) {
  if (!hasMeta(widget)) return null
  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {widget.coverage ? <CoverageMeter coverage={widget.coverage} noun={noun} /> : null}
        <WarningChip check={widget.check} onApply={onApplyFix} />
      </div>
      {/* Said in words under the ring, as the figure cards do: what was left out and why. */}
      <PartialNote widget={widget} />
    </>
  )
}

/** One bar of a list: label, a track with a fill, the engine's digits. */
export function BarRow({
  label,
  value,
  share,
  index,
  muted = false,
  unit,
  shown,
  lead = false,
}: {
  label: string
  value: string | null
  share: number
  index: number
  muted?: boolean
  unit: Drawn['unit']
  /** The digits drawn, where not the value in its unit: a breakdown's compact amount. */
  shown?: string | undefined
  /** The longest bar of its list: its digits in semibold. */
  lead?: boolean
}) {
  const negative = value?.startsWith('-') ?? false
  return (
    <li
      data-role={muted ? 'part-left-out' : 'part'}
      className="grid grid-cols-[minmax(0,--spacing(28))_minmax(0,1fr)_auto] items-center gap-3 text-body-s"
    >
      <span className={cn('truncate', muted ? 'text-muted-foreground' : 'text-foreground')}>
        {label}
      </span>
      <span aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-track">
        {muted ? null : (
          <BarFill
            ratio={share}
            index={index}
            className={negative ? 'bg-market-down-fill' : 'bg-tone-green'}
          />
        )}
      </span>
      <span
        className={cn(
          'text-right tabular-nums',
          muted ? 'text-muted-foreground' : 'text-foreground',
          lead && 'font-semibold',
        )}
      >
        {muted ? '—' : (shown ?? shownValue(value, unit))}
      </span>
    </li>
  )
}

/** One total's parts, company by company: top 8, "+N more", left-out greyed. */
function PartBars({
  part,
  caption,
  unit,
}: {
  part: BreakdownPart
  caption: string
  unit: Drawn['unit']
}) {
  const { parts, more, leftOut } = contributionsOf(part)
  if (parts.length === 0 && leftOut.length === 0) return null
  return (
    <div data-role="contribution" className="flex flex-col gap-2">
      <p data-role="part-caption" className="text-label-s text-muted-foreground">
        {caption}
      </p>
      <ul aria-label={caption} className="flex flex-col gap-2">
        {parts.map((row, index) => (
          <BarRow
            key={row.label}
            label={row.label}
            value={row.value}
            share={row.share}
            index={index}
            unit={unit}
            shown={row.value === null ? undefined : compactDigits(row.value)}
          />
        ))}
        {leftOut.map((row, index) => (
          <BarRow
            key={row.label}
            label={row.label}
            value={null}
            share={0}
            index={parts.length + index}
            muted
            unit={unit}
          />
        ))}
      </ul>
      {more > 0 ? (
        <p data-role="parts-more" className="text-label-s text-muted-foreground">
          +{String(more)} more
        </p>
      ) : null}
    </div>
  )
}

/**
 * A figure opened: each of its totals' parts as bars, captioned in plain
 * text from the picture ("Σ Shares × EPS"), never a second picture.
 */
function Contributions({ widget }: { widget: Drawn }) {
  const aggregates = widget.check?.picture ? aggregatesOf(widget.check.picture) : []
  const captions =
    aggregates.length === widget.breakdown.length ? aggregates.map(pictureText) : null
  return (
    <div data-role="contributions" className="mt-3 flex flex-col gap-4">
      {widget.breakdown.map((part, index) => (
        <PartBars
          key={`${part.part}-${String(index)}`}
          part={part}
          caption={captions?.[index] ?? part.part}
          unit={widget.unit}
        />
      ))}
    </div>
  )
}

/**
 * The rows a figure leaves out, wherever the engine names them: a column's or a
 * list's points with no value, a total's parts that did not count. Each with the
 * figures it lacks.
 */
function leftOutRows(widget: Drawn): { label: string; missing: string[] }[] {
  const rows = new Map<string, string[]>()
  for (const point of widget.points ?? []) {
    if (point.value === null) rows.set(point.label, point.missing)
  }
  for (const part of widget.breakdown) {
    for (const row of part.rows) {
      if (!row.counted) rows.set(row.label, [...(rows.get(row.label) ?? []), ...row.missing])
    }
  }
  return [...rows].map(([label, missing]) => ({ label, missing: [...new Set(missing)] }))
}

/**
 * The rows left out, ten a page. Where every one lacks the same figure the note
 * above already says so, so each row is only its name; the reason is repeated
 * on a row only where the rows differ.
 */
function LeftOutRows({
  rows,
  labels,
  noteSaysWhy,
}: {
  rows: { label: string; missing: string[] }[]
  labels: ReadonlyMap<string, string>
  /** The note above already names what the rows lack. */
  noteSaysWhy: boolean
}) {
  const [page, setPage] = usePage([rows])
  const reason = (row: { missing: string[] }) =>
    row.missing.length > 0
      ? `no ${row.missing.map((name) => labels.get(name) ?? name).join(' or ')}`
      : 'no figure'
  const first = rows[0]
  const uniform =
    noteSaysWhy && first !== undefined && rows.every((row) => reason(row) === reason(first))
  // At most a phone's width: on a full-width table a row's name and reason would sit a page apart.
  return (
    <div className="max-w-md">
      <ul data-role="left-out-rows" className="mt-2 flex flex-col gap-1">
        {pageOf(rows, page).map((row) => (
          <li key={row.label} className="flex justify-between gap-3 text-muted-foreground">
            <span className="text-foreground">{row.label}</span>
            {uniform ? null : <span>{reason(row)}</span>}
          </li>
        ))}
      </ul>
      <Pager page={page} total={rows.length} onPage={setPage} />
    </div>
  )
}

/** A disclosure's chevron, after its words so the words keep the content's left edge. */
function Chevron({ open }: { open?: boolean }) {
  return (
    <ChevronDown
      aria-hidden="true"
      className={cn(
        'size-4 shrink-0 transition-transform group-open:rotate-180',
        open && 'rotate-180',
      )}
    />
  )
}

/**
 * What a figure leaves out, in words ("Left out: no Cash div/share"), and the
 * rows behind it where the engine names them. Null where every row counts, or
 * where the note would be a bare "Left out" with no rows to open: that says no
 * more than the ring.
 */
function leftOutOf(
  widget: Drawn,
  expandable = true,
): {
  note: string
  gone: { label: string; missing: string[] }[]
  labels: ReadonlyMap<string, string>
  noteSaysWhy: boolean
} | null {
  const labels = widget.check?.picture ? labelsOf(widget.check.picture) : new Map<string, string>()
  // A per-company table carries its left-out lists on its rows, not on the widget.
  const why = Array.isArray(widget.left_out_for) ? widget.left_out_for : []
  const note = partialNote(widget.coverage, why, labels)
  if (note === null) return null
  const gone = expandable ? leftOutRows(widget) : []
  if (gone.length === 0 && note === 'Left out') return null
  return { note, gone, labels, noteSaysWhy: why.length > 0 }
}

/**
 * Said in words where the figure counts only some of its rows. Where the engine
 * names the rows left out, the note itself is the dropdown that lists them and
 * what each lacks -- unless the card already shows them (`expandable` off).
 */
function PartialNote({ widget, expandable = true }: { widget: Drawn; expandable?: boolean }) {
  const left = leftOutOf(widget, expandable)
  if (left === null) return null
  if (left.gone.length === 0) {
    return (
      <p data-role="partial-note" className="mt-1 text-label-s text-warning">
        {left.note}
      </p>
    )
  }
  return (
    <details data-role="left-out" className="group mt-1 text-label-s">
      <summary
        data-role="partial-note"
        className="inline-flex cursor-pointer list-none items-center gap-1 text-warning"
      >
        {left.note}
        <Chevron />
      </summary>
      <LeftOutRows rows={left.gone} labels={left.labels} noteSaysWhy={left.noteSaysWhy} />
    </details>
  )
}

/** How many of a figure's biggest parts its closed card draws. */
const PREVIEW = 3

/**
 * What a closed figure card draws of its parts, and whether opening it shows
 * more. A sum whose biggest parts are a telling share of it: the share bar.
 * Otherwise, from two parts up, the three biggest as thin bars. A single part
 * is the figure itself, so nothing. Opening is offered only where it shows
 * more than the closed card already does.
 */
function partsOf(widget: Drawn): { share: Share | null; preview: Part[]; more: boolean } {
  const part = widget.breakdown[0]
  if (!part) return { share: null, preview: [], more: false }
  const { parts } = contributionsOf(part, Number.POSITIVE_INFINITY)
  const top = parts.slice(0, PREVIEW)
  const share = part.name === 'sum' ? topShare(part, top) : null
  const preview = share || parts.length < 2 ? [] : top
  return { share, preview, more: parts.length > 1 && (share !== null || parts.length > PREVIEW) }
}

/** The biggest parts of a figure as thin bars, without opening it. The digits are the engine's. */
function PartsPreview({ parts, title }: { parts: Part[]; title: string }) {
  if (parts.length === 0) return null
  return (
    <ul
      data-role="parts-preview"
      aria-label={`Biggest parts of ${title}`}
      className="mt-3 flex flex-col gap-2"
    >
      {parts.map((row, index) => (
        <li
          key={row.label}
          data-role="part-preview"
          className="grid grid-cols-[minmax(0,--spacing(20))_minmax(0,1fr)_auto] items-center gap-2 text-label-s"
        >
          <span className="truncate text-muted-foreground">{row.label}</span>
          <span aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-track">
            <BarFill
              ratio={row.share}
              index={index}
              className={row.value?.startsWith('-') ? 'bg-market-down-fill' : 'bg-tone-green'}
            />
          </span>
          <span className="text-right tabular-nums text-muted-foreground">
            {row.value === null ? '—' : compactDigits(row.value)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** The bar's own tones, so each swatch in the legend is the colour of its segment. */
const SHARE_SWATCH = {
  accent: 'bg-tone-green',
  teal: 'bg-tone-teal',
  violet: 'bg-tone-violet',
  neutral: 'bg-muted-foreground-subtle',
} as const

/**
 * How much of a sum its biggest parts make: a segmented bar, and a legend of
 * each part's share and the rest's. No sentence: "Top 3 are 62%" is the
 * legend's own numbers again.
 */
function TopShare({ share, title }: { share: Share; title: string }) {
  return (
    <div data-role="top-share" className="mt-3 flex flex-col gap-1">
      <SegmentedBar segments={share.segments} height={8} ariaLabel={`Biggest parts of ${title}`} />
      <ul data-role="top-share-legend" className="flex flex-wrap gap-x-3 gap-y-1 text-label-s">
        {share.segments.map((segment) => (
          <li key={segment.label} className="flex items-center gap-1 text-muted-foreground">
            <span aria-hidden="true" className={cn('size-2', SHARE_SWATCH[segment.tone])} />
            <span>{segment.label}</span>
            <span className="tabular-nums">{String(segment.percent)}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function FigureCard({
  widget,
  actions = null,
  noun = 'holdings',
  onApplyFix,
}: {
  widget: Drawn
  actions?: ReactNode
  /** What the rows are, for the coverage meter's words. */
  noun?: string
  onApplyFix?: ((fix: Fix) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const { share, preview, more: hasParts } = partsOf(widget)
  return (
    // Full width while its parts are open: a half-width tile squeezes the bars.
    <article
      id={widget.id ? `measure-${widget.id}` : undefined}
      data-role="dash-tile"
      className={cn(SECTION_CARD, 'min-w-0', open && 'col-span-full')}
    >
      <Head widget={widget} actions={actions} />
      {/* The picture first, straight under the title; opened, drawn large. */}
      {widget.error ? null : <Picture widget={widget} size={open ? 'lg' : 'sm'} />}
      {widget.error ? (
        <Unreadable widget={widget} />
      ) : (
        <>
          <div
            className={cn(
              // One row on every card: the value and the open chevron, its
              // glyph on the right content edge. Coverage is a quiet line of
              // its own under it: beside a large value it truncated to "16 …".
              'mt-3 flex min-h-8 items-center gap-x-3',
              hasParts && 'cursor-pointer',
            )}
            onClick={
              hasParts
                ? () => {
                    setOpen(!open)
                  }
                : undefined
            }
          >
            <p
              data-role="tile-value"
              className="shrink-0 text-h-l whitespace-nowrap text-foreground tabular-nums"
            >
              {shownValue(widget.value ?? null, widget.unit)}
            </p>
            {hasParts ? (
              <button
                type="button"
                aria-expanded={open}
                aria-label={
                  open ? `Hide the parts of ${widget.title}` : `Show the parts of ${widget.title}`
                }
                onClick={(event) => {
                  event.stopPropagation()
                  setOpen(!open)
                }}
                className={cn(
                  '-mr-2 ml-auto inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted',
                  FOCUS,
                )}
              >
                <ChevronDown className={cn('size-4', open && 'rotate-180')} aria-hidden="true" />
              </button>
            ) : null}
          </div>
          {widget.coverage && partial(widget.coverage) ? (
            <div data-role="tile-coverage" className="mt-1 flex min-w-0">
              <CoverageMeter coverage={widget.coverage} noun={noun} />
            </div>
          ) : null}
          {widget.check?.warnings.length ? (
            <div className="mt-2">
              <WarningChip check={widget.check} onApply={onApplyFix} />
            </div>
          ) : null}
          <PartialNote widget={widget} expandable={!open} />
          {open ? null : share ? (
            <TopShare share={share} title={widget.title} />
          ) : (
            <PartsPreview parts={preview} title={widget.title} />
          )}
        </>
      )}
      {open ? (
        <Reveal variant="fade-up">
          <Contributions widget={widget} />
        </Reveal>
      ) : null}
    </article>
  )
}

export function Pager({
  page,
  total,
  onPage,
}: {
  page: number
  total: number
  onPage: (page: number) => void
}) {
  if (total <= PAGE_SIZE) return null
  const last = Math.ceil(total / PAGE_SIZE) - 1
  const from = page * PAGE_SIZE + 1
  const to = Math.min(total, from + PAGE_SIZE - 1)
  return (
    <div data-role="pager" className="mt-2 flex items-center justify-end gap-1">
      <IconButton
        aria-label="Previous page"
        className="size-8"
        disabled={page === 0}
        onClick={() => {
          onPage(page - 1)
        }}
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
      </IconButton>
      <span data-role="page-range" className="font-mono text-label-s text-muted-foreground">
        {`${String(from)}–${String(to)} / ${String(total)}`}
      </span>
      <IconButton
        aria-label="Next page"
        className="size-8"
        disabled={page >= last}
        onClick={() => {
          onPage(page + 1)
        }}
      >
        <ChevronRight className="size-4" aria-hidden="true" />
      </IconButton>
    </div>
  )
}

export function BarsCard({
  widget,
  actions = null,
  noun = 'holdings',
  onApplyFix,
}: {
  widget: Drawn
  actions?: ReactNode
  noun?: string
  onApplyFix?: ((fix: Fix) => void) | undefined
}) {
  const points = widget.points ?? []
  const [page, setPage] = usePage([widget.points])
  // Every bar against the longest in the WHOLE list, not the page.
  const values = points.map((p) => p.value)
  return (
    <article
      id={widget.id ? `measure-${widget.id}` : undefined}
      data-role="dash-bars"
      className={cn(SECTION_CARD, 'min-w-0')}
    >
      <Head widget={widget} actions={actions} />
      {widget.error ? null : <Picture widget={widget} />}
      {widget.error ? (
        <Unreadable widget={widget} />
      ) : (
        <>
          <Meta widget={widget} noun={noun} onApplyFix={onApplyFix} />
          <ul aria-label={widget.title} className="mt-3 flex flex-col gap-2">
            {pageOf(points, page).map((point, index) => {
              const share = barShare(point.value, values)
              return (
                <BarRow
                  key={point.label}
                  label={point.label}
                  value={point.value}
                  share={share}
                  index={index}
                  muted={point.value === null}
                  lead={point.value !== null && share === 1}
                  unit={widget.unit}
                />
              )
            })}
          </ul>
          <Pager page={page} total={points.length} onPage={setPage} />
        </>
      )}
    </article>
  )
}

const SCOPE_ICON = {
  book: Briefcase,
  watchlist: Eye,
  market: Globe,
  symbols: ListChecks,
  brokers: ArrowLeftRight,
} as const

/**
 * What a table is over, as an icon and its name ("My holdings", a
 * watchlist's name, "The market"). No count: the rows are right below, and
 * past a page the pager says how many.
 */
function ScopeCaption({ scope }: { scope: Pick<ScopeRows, 'kind' | 'label'> }) {
  const Icon = SCOPE_ICON[scope.kind]
  return (
    <p
      data-role="scope-caption"
      className="flex min-w-0 items-center gap-1 text-label-s text-muted-foreground"
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{scope.label}</span>
    </p>
  )
}

/** One numeric cell: the engine's digits over a thin bar, the column's leader in semibold. */
function BarCell({
  value,
  share,
  unit,
}: {
  value: string | null
  share: number
  unit: Drawn['unit']
}) {
  if (value === null) {
    return <span className="text-muted-foreground">—</span>
  }
  const negative = value.startsWith('-')
  return (
    <span className="inline-flex min-w-16 flex-col items-stretch gap-1">
      <span
        data-role="cell-value"
        className={cn('text-foreground', share === 1 && 'font-semibold')}
      >
        {shownValue(value, unit)}
      </span>
      <span
        data-role="cell-bar"
        aria-hidden="true"
        className="flex h-1 justify-end overflow-hidden rounded-full"
      >
        <BarFill
          as="span"
          ratio={share}
          from="right"
          data-share={String(share)}
          className={cn('rounded-full', negative ? 'bg-market-down-fill' : 'bg-tone-green')}
        />
      </span>
    </span>
  )
}

/**
 * One column's flag above a table, as ONE row on the card's left edge: its
 * title (where the table has more than one column), the coverage ring, a
 * warning, and "Left out ⌄" inline. The rows left out open under the row, not
 * beside it, so opening one never reflows the flags around it.
 */
function ColumnFlag({
  column,
  named,
  noun,
  onApplyFix,
}: {
  column: Drawn
  named: boolean
  noun: string
  onApplyFix?: ((fix: Fix) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const left = column.error ? null : leftOutOf(column)
  const listId = `left-out-${column.id ?? column.title.replace(/\W+/g, '-')}`
  return (
    <li data-role="column-flag" className="flex min-w-0 flex-col">
      <div
        data-role={left && left.gone.length > 0 ? 'left-out' : undefined}
        className="flex min-w-0 flex-col"
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-label-s">
          {named ? (
            <span data-role="flag-title" className="break-words text-foreground">
              {column.title}
            </span>
          ) : null}
          {column.error ? (
            <span className="text-destructive">{column.error.message}</span>
          ) : (
            <>
              {column.coverage ? <CoverageMeter coverage={column.coverage} noun={noun} /> : null}
              <WarningChip check={column.check} onApply={onApplyFix} />
              {left === null ? null : left.gone.length === 0 ? (
                <span data-role="partial-note" className="text-warning">
                  {left.note}
                </span>
              ) : (
                <button
                  type="button"
                  data-role="partial-note"
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => {
                    setOpen(!open)
                  }}
                  className={cn(
                    'inline-flex min-h-6 items-center gap-1 rounded-sm text-warning',
                    FOCUS,
                  )}
                >
                  {left.note}
                  <Chevron open={open} />
                </button>
              )}
            </>
          )}
        </div>
        {left && left.gone.length > 0 ? (
          <div id={listId} hidden={!open}>
            <LeftOutRows rows={left.gone} labels={left.labels} noteSaysWhy={left.noteSaysWhy} />
          </div>
        ) : null}
      </div>
    </li>
  )
}

/**
 * The column widgets over one scope, as one table: a row per company and a
 * column per widget, ten rows a page. Each value carries a thin bar against
 * the column's largest across every row, and the leader is in semibold, so
 * the table reads without a word. Sorting is the reader's choice of order
 * and changes no value; a missing value stays last either way. Nothing is
 * ranked until the reader sorts.
 */
export function ScopeTable({
  columns,
  scope,
  actions,
  onApplyFix,
}: {
  columns: Drawn[]
  scope: Pick<ScopeRows, 'kind' | 'label' | 'rows'>
  actions?: ((widget: Drawn) => ReactNode) | undefined
  onApplyFix?: ((widget: Drawn, fix: Fix) => void) | undefined
}) {
  const [sort, setSort] = useState<{ column: number; descending: boolean } | null>(null)
  const cells = useMemo(
    () =>
      columns.map(
        (column) =>
          new Map<string, Point>((column.points ?? []).map((point) => [point.label, point])),
      ),
    [columns],
  )
  // Each column's values over EVERY row of the scope: what a bar is measured against.
  const columnValues = useMemo(
    () =>
      columns.map((column, index) =>
        column.error ? [] : scope.rows.map((symbol) => cells[index]?.get(symbol)?.value ?? null),
      ),
    [columns, cells, scope.rows],
  )
  const order = useMemo(() => {
    const rows = [...scope.rows]
    if (sort) {
      const pick = (symbol: string) => cells[sort.column]?.get(symbol)?.value ?? null
      rows.sort((a, b) => {
        const va = pick(a)
        const vb = pick(b)
        if (va === null || vb === null) return compareValues(va, vb)
        return sort.descending ? compareValues(vb, va) : compareValues(va, vb)
      })
    }
    return rows
  }, [scope.rows, cells, sort])
  const [page, setPage] = usePage([
    sort?.column,
    sort?.descending,
    scope.rows,
    ...columns.map((c) => c.points),
  ])
  const noun = nounOf(scope.kind)
  const flagged = columns.filter((column) => Boolean(column.error) || hasMeta(column))
  const pictured = columns.filter((column) => !column.error)

  return (
    <article
      id={
        columns.find((c) => c.id)?.id ? `measure-${columns.find((c) => c.id)?.id ?? ''}` : undefined
      }
      data-role="dash-table"
      data-scope={scope.kind}
      // Every column's measure, so a link to any of them finds this table.
      data-measures={columns.flatMap((c) => (c.id ? [c.id] : [])).join(' ') || undefined}
      className={cn(SECTION_CARD, 'min-w-0')}
    >
      <ScopeCaption scope={scope} />
      {/* Only what needs the reader's eye before the rows: a column that does
          not read, leaves rows out, or has a warning. Every column's title is
          its header already. */}
      {flagged.length > 0 ? (
        <ul data-role="column-flags" className="mt-3 flex flex-col gap-2">
          {flagged.map((column) => (
            <ColumnFlag
              key={column.id ?? column.title}
              column={column}
              named={columns.length > 1}
              noun={noun}
              onApplyFix={
                onApplyFix
                  ? (fix) => {
                      onApplyFix(column, fix)
                    }
                  : undefined
              }
            />
          ))}
        </ul>
      ) : null}
      {/* What each column is worked out from, a tap away: the table leads. */}
      {pictured.length > 0 ? (
        <details data-role="column-legend" className="group mt-2 text-label-s">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-muted-foreground hover:text-foreground">
            {pictured.length === 1 ? 'Formula' : 'Formulas'}
            <Chevron />
          </summary>
          <ul className="mt-2 grid gap-3 @md:grid-cols-2">
            {pictured.map((column) => (
              <li key={column.id ?? column.title} className="flex min-w-0 flex-col">
                {columns.length > 1 ? (
                  <span className="text-label-s break-words text-foreground">{column.title}</span>
                ) : null}
                <Picture widget={column} />
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-body-s">
          <thead>
            <tr className="border-b border-border-subtle text-left text-muted-foreground">
              <th scope="col" className="py-2 pr-3 font-medium">
                <span className="sr-only">Company</span>
              </th>
              {columns.map((column, index) => {
                const active = sort?.column === index
                return (
                  <th
                    key={column.id ?? index}
                    scope="col"
                    aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}
                    className="py-2 pl-6 text-right align-bottom font-medium"
                  >
                    {/* The title's right edge IS the values' right edge: the ⋮,
                        Mitra's dot and the sort arrow sit before it, and the
                        title stays on one line, setting the column's width. */}
                    <span className="flex items-center justify-end gap-1">
                      {actions ? (
                        <span className="flex h-5 shrink-0 items-center">{actions(column)}</span>
                      ) : null}
                      <MitraDot origin={column.origin} />
                      <button
                        type="button"
                        aria-label={`Sort by ${column.title}`}
                        title={column.title}
                        onClick={() => {
                          setSort(
                            active
                              ? { column: index, descending: !sort.descending }
                              : { column: index, descending: true },
                          )
                        }}
                        className={cn(
                          'inline-flex min-h-8 items-center gap-1 rounded-sm text-right hover:text-foreground',
                          FOCUS,
                        )}
                      >
                        {active ? (
                          <span aria-hidden="true">{sort.descending ? '↓' : '↑'}</span>
                        ) : null}
                        <span data-role="column-title" className="whitespace-nowrap">
                          {column.title}
                        </span>
                      </button>
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageOf(order, page).map((symbol) => (
              <tr key={symbol} className="border-b border-border-subtle last:border-0">
                <th scope="row" className="py-2 pr-3 text-left font-medium text-foreground">
                  {symbol}
                </th>
                {columns.map((column, index) => {
                  const value = column.error ? null : (cells[index]?.get(symbol)?.value ?? null)
                  return (
                    <td key={column.id ?? index} className="py-2 pl-6 text-right tabular-nums">
                      <BarCell
                        value={value}
                        share={barShare(value, columnValues[index] ?? [])}
                        unit={column.unit}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={order.length} onPage={setPage} />
    </article>
  )
}

/** One widget alone, as the builder's and the editor's live preview draw it. */
export function WidgetView({
  widget,
  scopes,
  holdings,
}: {
  widget: Drawn
  scopes: readonly ScopeRows[]
  holdings: string[]
}) {
  const scope = scopes.find((s) => s.key === widget.scopeKey)
  const noun = nounOf(scope?.kind)
  if (isWindowed(widget)) return <WindowCard widget={widget} />
  if (widget.kind === 'number') return <FigureCard widget={widget} noun={noun} />
  if (widget.kind === 'column') {
    const rows =
      scope?.rows ?? (holdings.length ? holdings : (widget.points ?? []).map((p) => p.label))
    return (
      <ScopeTable
        columns={[widget]}
        scope={{ kind: scope?.kind ?? 'book', label: scope?.label ?? 'My holdings', rows }}
      />
    )
  }
  return <BarsCard widget={widget} noun={noun} />
}
